import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AuthService, AuthState, CookieService } from '../services';

export const isNode = typeof process !== 'undefined' && process.versions != null && process.versions.node != null;

@Injectable()
export class HttpInterceptorService implements HttpInterceptor {
  /**
   * Append bearer token to auth settings
   * @param settings
   */
  private auth = inject(AuthService);
  private cookieService = inject(CookieService);

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Handle transfer state between node and the browser
    // If is a browser and the request is GET

    const isPublicApi = req.url.includes('/public/');
    const isAuthEndpoint = req.url.includes('authLogin') || req.url.includes('sessionInfoPath') || req.url.includes('login/logout');
    const isSpotlightEndpoint = req.url.includes('spotlights');
    const isAuthenticated = this.auth.authState === AuthState.loggedIn;

    // Clone request, add headers
    let cloneReq: Observable<HttpEvent<any>>;
    if (req.headers.has('Authorization')) {
      cloneReq = next.handle(req.clone());
    } else if (isSpotlightEndpoint) {
      cloneReq = next.handle(req.clone({ withCredentials: isAuthenticated }));
    } else {
      cloneReq = next.handle(req.clone({ withCredentials: true }));
    }
    // Return request, handle errors globally here
    return cloneReq.pipe(
      tap(event => {
        // If SSR is on and this is on the server/node and this is a successful get request,
        // store the result in transfer state to pass to the browser
        /**
        // API responses are malformed on auth failures
        if ('SUCCESS' in event && (event as any).body?.SUCCESS === false) {
          this.sessionEnd();
        }
         */
      }),
      catchError(error => {
        console.error(error);

        const isTokenExpiredError =
          error?.error?.MESSAGE?.includes('Signature verification failed: Token expired') ||
          error?.body?.MESSAGE?.includes('Signature verification failed: Token expired');

        if (error.status === 401 || error.status === 403 || isTokenExpiredError) {
          if (isSpotlightEndpoint) {
            this.cookieService.clearAuthCookies();
          } else if (!isPublicApi || isAuthEndpoint) {
            this.sessionEnd();
          }
        }

        return throwError(error);
      }),
    );
  }

  /**
   * End the user's session based on auth failure
   */
  private sessionEnd() {
    this.auth.logOut(AuthState.sessionExpired);
  }
}
